import { ApiError, errorKeyForSuiteApiResult } from './errors';
import type { PortalClient } from './portal-client';
import {
  ESuiteApiResult,
  type OrderNoteAddRequest,
  type OrderNoteItem,
  type OrderNotesMarkAsReadRequest,
} from './types';

/** Outcome of {@link OrderNotesApi.addOrderNote}. */
export interface AddOrderNoteResult {
  /** Notes touched by the call (marked-as-read notes and/or the new note), as returned by the server. */
  notes: OrderNoteItem[];
  /**
   * True when the lab wrote new notes since the caller last synced: the server did NOT add the
   * submitted note, it returned the missed notes instead (mirrors the desktop/web Manager
   * case-chat conflict protocol). The caller should merge the notes in and let the user resend.
   */
  hasConflict: boolean;
}

/**
 * Order case-chat API — thin, typed wrapper reusing the existing Portal order-notes logic
 * (the same chat used by Dental.Manager / Dental.WebManager) via the SuiteAPI, so MDenture
 * customers read and write into the same conversation. No chat business logic lives here.
 *
 *   GET SuiteAPI/Order/GetOrderNotes
 *   PUT SuiteAPI/Order/OrderNoteAdd
 *   PUT SuiteAPI/Order/OrderNotesMarkAsRead
 *
 * See Server/Portal/Areas/SuiteAPI/Controllers/OrderController.cs for the Portal-side implementation.
 */
export class OrderNotesApi {
  private readonly client: PortalClient;

  public constructor(client: PortalClient) {
    this.client = client;
  }

  /**
   * Fetches the case-chat notes for an order, oldest first.
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async getOrderNotes(
    orderId: string,
    sinceUtc?: string,
    signal?: AbortSignal,
  ): Promise<OrderNoteItem[]> {
    const query: Record<string, string> = { orderID: orderId };
    if (sinceUtc) {
      query.sinceUTC = sinceUtc;
    }

    const result = await this.client.request<OrderNoteItem[]>({
      method: 'GET',
      path: '/SuiteAPI/Order/GetOrderNotes',
      query,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data ?? [];
  }

  /**
   * Adds a case-chat note. Resolves with `hasConflict: true` (instead of throwing) when the other
   * side wrote new notes first — see {@link AddOrderNoteResult}.
   *
   * @throws {ApiError} with a translatable key on any other failure.
   */
  public async addOrderNote(
    orderId: string,
    content: string,
    unreadNoteIds: readonly string[],
    signal?: AbortSignal,
  ): Promise<AddOrderNoteResult> {
    const body: OrderNoteAddRequest = {
      orderID: orderId,
      content,
      unreadNoteIds: [...unreadNoteIds],
      isCustomer: true,
      sendEmailNotification: true,
    };
    const result = await this.client.request<OrderNoteItem[]>({
      method: 'PUT',
      path: '/SuiteAPI/Order/OrderNoteAdd',
      body,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult === ESuiteApiResult.NewNotesAvailable) {
      return { notes: result.data ?? [], hasConflict: true };
    }
    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return { notes: result.data ?? [], hasConflict: false };
  }

  /**
   * Marks the given notes as read (no-op when the list is empty).
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async markNotesAsRead(
    orderId: string,
    unreadNoteIds: readonly string[],
    signal?: AbortSignal,
  ): Promise<OrderNoteItem[]> {
    if (unreadNoteIds.length === 0) {
      return [];
    }

    const body: OrderNotesMarkAsReadRequest = { orderID: orderId, unreadNoteIds: [...unreadNoteIds] };
    const result = await this.client.request<OrderNoteItem[]>({
      method: 'PUT',
      path: '/SuiteAPI/Order/OrderNotesMarkAsRead',
      body,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data ?? [];
  }
}
